import { Controller } from "@hotwired/stimulus"

// Params that describe what the table is showing -- sort, grouping, trend
// direction, pagination. The nested table frame (pi-insights-trends) changes
// most of them on its own, and Group by changes on a full-page visit; either
// way they reach the page URL but not this report frame's src.
const TABLE_PARAMS = ["page", "sort_by", "sort_direction", "direction", "group_by"]

// Table state for links rendered OUTSIDE the table frame (Group by in the
// toolbar; the map, map-metric chips and trend stat cards in the report). The
// server renders those with only the table params they set on purpose
// (insights_outer_url); at click time syncLink fills every other one from the
// page URL, which the table frame keeps current. Page is deliberately absent:
// those links start again from page 1.
const LINK_SYNC_PARAMS = ["sort_by", "sort_direction", "direction", "group_by"]

export default class extends Controller {
  static targets = ["report", "error"]
  // Capture-phase document click, before Turbo follows a same-page Insights link
  // from outside the table frame: a table param present in the link was set by it
  // and is kept; an absent one follows the current page URL.
  syncLink(event) {
    const link = event.target?.closest?.("a[href]")
    if (!link || link.closest("turbo-frame#pi-insights-trends")) return

    const url = new URL(link.getAttribute("href"), window.location.href)
    if (url.pathname !== new URL(window.location.href).pathname) return

    const current = new URL(window.location.href).searchParams
    LINK_SYNC_PARAMS.forEach(key => {
      if (url.searchParams.has(key) || !current.has(key)) return
      url.searchParams.set(key, current.get(key))
    })
    link.setAttribute("href", url.pathname + url.search + url.hash)
  }

  failed(event) {
    // The nested table frame fails the same way: show the same retry alert
    // instead of leaving the old table up silently.
    if (!this.ownFrame(event)) return
    event.preventDefault()
    this.reportTarget.classList.add("pi-insights-failed")
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
  }

  loaded(event) {
    if (event && !this.ownFrame(event)) return
    this.reportTarget.classList.remove("pi-insights-failed")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  // Retry what is on screen (after a sort/page the report src is stale), not
  // the report as first loaded.
  retry() {
    this.loaded()
    this.load(this.currentSource())
  }

  // Fired by pi.insights.changed after a provider is saved: same request as on
  // screen, bypassing the evidence cache.
  refresh() {
    if (!this.hasReportTarget || !this.reportTarget.src) return
    const source = this.currentSource()
    source.searchParams.set("refresh", "1")
    this.load(source)
  }

  // The report src rebuilt for what is on screen: its own scope params (state,
  // compare, map metric, ...), with the table params replaced outright by the
  // page URL's -- a table param the navigation dropped (page after a sort)
  // must not survive from the old src.
  currentSource() {
    const source = new URL(this.reportTarget.src, window.location.href)
    const page = new URL(window.location.href).searchParams
    source.searchParams.delete("refresh")
    TABLE_PARAMS.forEach(key => source.searchParams.delete(key))
    page.forEach((value, key) => { if (key !== "refresh") source.searchParams.set(key, value) })
    return source
  }

  // An unchanged src would not refetch on its own, so reload in place. Compare
  // resolved URLs: the attribute can hold a relative path.
  load(source) {
    const current = new URL(this.reportTarget.src, window.location.href).href
    if (current === source.href) this.reportTarget.reload()
    else this.reportTarget.src = source.href
  }

  ownFrame(event) {
    return event.target === this.reportTarget || this.reportTarget.contains?.(event.target)
  }
}
